import { Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Injectable, NgZone } from '@angular/core';
import { LMUrlUtilService } from '@services/utils';
import { SohoModalDialogService } from 'ids-enterprise-ng';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMDialogButtonKey, LMUniqueIdService } from '@app/utils';
import { LMActionFormDialogResult, LMActionFormDialogResultCode, LMFormContext } from '@app/components';
import { LMLoggerService } from '@app/services';
import { LMMenuSecurityComponent } from '@components/menusecurity/lm-menu-security.component';
import { LMTransactionService } from '@app/transaction';
import { LMMenuSecurityInfoComponent } from '@components/menusecurity/lm-menu-security-info.component';


@Injectable()
export class LMMenuSecurityService {

  constructor(
    protected httpClient: HttpClient,
    protected sohoModalDialogService: SohoModalDialogService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmThemeService: LMThemeService,
    protected lmLoggerService: LMLoggerService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected lmTransactionService: LMTransactionService) {
  }

  public openWebAppComponentInfoDialog(data: LMWebAppComponent) {
    let menuSecurityComponent: LMMenuSecurityInfoComponent;
    const formName = 'menuSecurityInfo';

    const buttons = [];
    buttons.push(
      {
        id: this.lmUniqueIdService.uniqueId([ formName, LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: () => {
          const result = {
            result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
          } as LMActionFormDialogResult;

          modalRef.close();
        },
      }
    );

    const title = data.name;

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-menu-security-info-modal`
    } ];

    const sohoModalOptions: SohoModalOptions = {
      attributes
    };

    const modalRef = this.sohoModalDialogService.modal(LMMenuSecurityInfoComponent, undefined, sohoModalOptions)
      .title(title)
      .buttons(buttons)
      .isAlert(false)
      .open()
      .apply((dialogComponent: LMMenuSecurityInfoComponent) => {
        menuSecurityComponent = dialogComponent;
        menuSecurityComponent.lmWebAppComponent = data;
      })
      .beforeClose(() => {
        if (!modalRef.dialogResult) {
          return true;
        }
      });
  }

  public openWebAppComponentItemInfoDialog(data: LMWebAppComponentItem) {
    let menuSecurityComponent: LMMenuSecurityInfoComponent;
    const formName = 'menuSecurityInfo';

    const buttons = [];
    buttons.push(
      {
        id: this.lmUniqueIdService.uniqueId([ formName, LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: () => {
          const result = {
            result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
          } as LMActionFormDialogResult;

          modalRef.close();
        },
      }
    );

    const title = data.name;

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-menu-security-info-modal`
    } ];

    const sohoModalOptions: SohoModalOptions = {
      attributes
    };

    const modalRef = this.sohoModalDialogService.modal(LMMenuSecurityInfoComponent, undefined, sohoModalOptions)
      .title(title)
      .buttons(buttons)
      .isAlert(false)
      .open()
      .apply((dialogComponent: LMMenuSecurityInfoComponent) => {
        menuSecurityComponent = dialogComponent;
        menuSecurityComponent.lmWebAppComponentItem = data;
      })
      .beforeClose(() => {
        if (!modalRef.dialogResult) {
          return true;
        }
      });
  }

  public openMenuSecurityDialog(request: LMMenuSecurityRequest, formContext: LMFormContext, actionSpec: LMActionSpec): Observable<boolean> {
    return new Observable<boolean>(observer => {
      let menuSecurityComponent: LMMenuSecurityComponent;
      const formName = 'menuSecurity';

      const buttons = [];
      buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ formName, LMDialogButtonKey.CLOSE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
          click: () => {
            const result = {
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL
            } as LMActionFormDialogResult;

            observer.next(false);
            observer.complete();
            modalRef.close();
          },
        }
      );

      buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ formName, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          click: () => {

            const dataView = menuSecurityComponent.getDataView();

            const result = {
              result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OK
            } as LMActionFormDialogResult;
            observer.next(true);
            this.lmTransactionService.runActionForm(formContext, actionSpec, dataView, [ dataView ]).subscribe(value => {
              observer.next(true);
              observer.complete();
              modalRef.close();
            });

          },
          isDefault: true,
        }
      );

      const title = Soho.Locale.translate('WebApplicationAccessibility');

      const attributes: SohoAttributesArray = [ {
        name: 'data-automation-id',
        value: `lm-menu-security-modal`
      } ];

      const sohoModalOptions: SohoModalOptions = {
        fullsize: 'always',
        attributes
      };

      const modalRef = this.sohoModalDialogService.modal(LMMenuSecurityComponent, undefined, sohoModalOptions)
        .title(title)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((dialogComponent: LMMenuSecurityComponent) => {
          menuSecurityComponent = dialogComponent;
          menuSecurityComponent.options = request;
        })
        .beforeClose(() => {
          // if ref.dialogResult is undefined, the escape key was used
          if (!modalRef.dialogResult) {
            observer.next(false);
            observer.complete();
            return true;
          }
        });
    });
  }

  public getMenuSecurityData(request: LMMenuSecurityRequest): Observable<LMMenuSecurityResponse> {
    const url = this.buildDataUrl(request);

    const params = {
      requestType: 'menuSecurity',
      webApp: request.webApp,
      role: request.role
    };

    return <Observable<LMMenuSecurityResponse>>this.httpClient.post(url, params).pipe(
      catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
  }

  private buildDataUrl(request: LMMenuSecurityRequest): string {
    const url =  this.lmUrlUtilService.getBaseUrl() + '/config/MenuSecurity?requestType=menuSecurity'; // adding this to url param for tracking usage
    return this.lmUrlUtilService.updateContextSessionKeyParams(url);
  }
}
